import { useState, useEffect, useCallback } from 'react';
import {
  Settings,
  Phone,
  MessageSquare,
  Webhook,
  Key,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Image,
  ExternalLink,
  AlertTriangle,
  Copy,
  FileText,
  Clock,
  Send,
  Search,
  Filter,
  Play,
  ChevronDown,
  ChevronRight,
  Activity,
  Zap,
  Info,
  Users,
  Save
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient, getPublicBaseUrl } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import TemplateBuilder from '@/components/messaging/TemplateBuilder';
import MediaLibrary from '@/components/messaging/MediaLibrary';
import { getErrorMessage } from '../../lib/utils';


interface TwilioConfig {
  configured: boolean;
  enabled?: boolean;
  account_sid?: string;
  has_auth_token?: boolean;
  has_api_key?: boolean;
  api_key?: string;
  updated_at?: string;
  mcp_synced?: boolean;
  connection_status?: 'healthy' | 'unhealthy' | 'unknown' | 'not_configured';
}

interface LocalPhoneNumber {
  id: string;
  phone_number: string;
  type: 'sms' | 'whatsapp' | 'both';
  label?: string;
  is_default: boolean;
  created_at?: string;
  source?: string;
}

interface TwilioPhoneNumber {
  id: string;
  phone_number: string;
  twilio_sid: string;
  friendly_name?: string;
  country_code?: string;
  number_type?: string;
  capabilities?: { sms?: boolean; mms?: boolean; voice?: boolean };
  status: string;
  monthly_cost?: number;
  webhook_configured: boolean;
  sms_url?: string;
  status_callback_url?: string;
}

interface TwilioAvailableNumber {
  phoneNumber: string;
  capabilities?: { sms?: boolean; mms?: boolean; voice?: boolean };
}

interface AssignSender {
  id: string;
  channel_type?: string;
  phone_number?: string;
  display_name?: string;
  is_global?: boolean;
}

interface AssignTenantUser {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

interface SenderAssignment {
  user_id: string;
}

interface WhatsAppSender {
  id: string;
  sender_id: string;
  phone_number?: string;
  display_name: string;
  status: string;
  verification_status?: string;
  quality_rating?: string;
  messaging_limit?: string;
  webhook_configured: boolean;
}

interface AddPhoneDialogState {
  open: boolean;
  editing: LocalPhoneNumber | null;
}

interface WhatsAppTemplate {
  id: string;
  friendly_name: string;
  category: 'UTILITY' | 'AUTHENTICATION' | 'MARKETING';
  language: string;
  body_text: string;
  approval_status: 'draft' | 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  content_sid?: string;
  created_at: string;
  submitted_at?: string;
  approved_at?: string;
}


interface ApiTestResult {
  name: string;
  category: 'account' | 'sms' | 'whatsapp' | 'templates' | 'senders' | 'webhook';
  status: 'success' | 'error' | 'warning' | 'skipped';
  message: string;
  details?: Record<string, any>;
  durationMs?: number;
  error?: string;
}

interface TestSummary {
  total: number;
  success: number;
  error: number;
  warning: number;
  skipped: number;
  totalDurationMs: number;
}

interface WebhookTestResult {
  success: boolean;
  webhookType: string;
  testPayload: Record<string, any>;
  results: ApiTestResult[];
  samplePayloads: Record<string, any>;
}

export default function MessagingSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [config, setConfig] = useState<TwilioConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);

  const [phoneNumbers, setPhoneNumbers] = useState<LocalPhoneNumber[]>([]);
  const [phoneDialog, setPhoneDialog] = useState<AddPhoneDialogState>({ open: false, editing: null });
  const [phoneForm, setPhoneForm] = useState<{ phone_number: string; type: 'sms' | 'whatsapp' | 'both'; label: string; is_default: boolean }>({ phone_number: '', type: 'sms', label: '', is_default: false });
  const [savingPhone, setSavingPhone] = useState(false);
  
  const [credentials, setCredentials] = useState({
    account_sid: '',
    auth_token: '',
    api_key: '',
    api_secret: ''
  });

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateBuilderOpen, setTemplateBuilderOpen] = useState(false);
  const [templateFilter, setTemplateFilter] = useState<string>('all');
  const [syncing, setSyncing] = useState(false);
  const [templateActionLoading, setTemplateActionLoading] = useState<string | null>(null);


  const [twilioNumbers, setTwilioNumbers] = useState<TwilioPhoneNumber[]>([]);
  const [whatsappSenders, setWhatsappSenders] = useState<WhatsAppSender[]>([]);
  const [syncingNumbers, setSyncingNumbers] = useState(false);
  const [syncingSenders, setSyncingSenders] = useState(false);
  const [numbersTab, setNumbersTab] = useState<'twilio' | 'whatsapp'>('twilio');
  const [purchaseNumberOpen, setPurchaseNumberOpen] = useState(false);
  const [addSenderOpen, setAddSenderOpen] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({ country: 'US', areaCode: '', numberType: 'local' });
  const [senderForm, setSenderForm] = useState({ senderId: '', displayName: '' });
  const [purchasing, setPurchasing] = useState(false);
  const [addingSender, setAddingSender] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState<TwilioAvailableNumber[]>([]);
  const [searchingNumbers, setSearchingNumbers] = useState(false);

  const [apiTestResults, setApiTestResults] = useState<ApiTestResult[]>([]);
  const [apiTestSummary, setApiTestSummary] = useState<TestSummary | null>(null);
  const [apiTestRunning, setApiTestRunning] = useState(false);
  const [webhookTestResults, setWebhookTestResults] = useState<ApiTestResult[]>([]);
  const [webhookTestRunning, setWebhookTestRunning] = useState(false);
  const [webhookTestType, setWebhookTestType] = useState<string>('sms_incoming');
  const [webhookTestPayload, setWebhookTestPayload] = useState<string>('');
  const [expandedTests, setExpandedTests] = useState<Set<string>>(new Set());

  const [assignSenders, setAssignSenders] = useState<AssignSender[]>([]);
  const [assignTenantUsers, setAssignTenantUsers] = useState<AssignTenantUser[]>([]);
  const [assignData, setAssignData] = useState<Record<string, string[]>>({});
  const [assignSaving, setAssignSaving] = useState<string | null>(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignGlobal, setAssignGlobal] = useState<Record<string, boolean>>({});

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, phonesRes] = await Promise.all([
        apiClient.get<TwilioConfig>('/messaging/settings'),
        apiClient.get<{ senders: LocalPhoneNumber[] }>('/messaging/senders?adminView=true')
      ]);
      
      if (configRes.success && configRes.data) {
        setConfig(configRes.data);
        if (configRes.data.account_sid) {
          setCredentials(prev => ({
            ...prev,
            account_sid: configRes.data!.account_sid! || '',
            api_key: configRes.data!.api_key! || ''
          }));
        }
      }
      if (phonesRes.success && phonesRes.data?.senders) {
        setPhoneNumbers(phonesRes.data.senders);
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile caricare la configurazione',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const res = await apiClient.get<{ templates: WhatsAppTemplate[] }>('/messaging/templates');
      if (res.success && res.data?.templates) {
        setTemplates(res.data.templates);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadTwilioNumbers = async () => {
    try {
      const res = await apiClient.get<{ senders: TwilioPhoneNumber[] }>('/messaging/senders?channel=sms&adminView=true');
      if (res.success && res.data?.senders) {
        setTwilioNumbers(res.data.senders);
      }
    } catch (error) {
      console.error('Failed to load Twilio numbers:', error);
    }
  };

  useEffect(() => {
    loadConfig();
    loadTemplates();
    loadTwilioNumbers();
  }, [loadConfig]);

  const loadWhatsAppSenders = async () => {
    try {
      const res = await apiClient.get<{ senders: WhatsAppSender[] }>('/messaging/senders?channel=whatsapp&adminView=true');
      if (res.success && res.data?.senders) {
        setWhatsappSenders(res.data.senders);
      }
    } catch (error) {
      console.error('Failed to load WhatsApp senders:', error);
    }
  };

  const handleSyncTwilioNumbers = async () => {
    setSyncingNumbers(true);
    try {
      const res = await apiClient.post<{ synced: number }>('/messaging/twilio-numbers/sync');
      if (res.success) {
        toast({ title: 'Sincronizzazione completata', description: `${res.data?.synced || 0} numeri sincronizzati` });
        loadTwilioNumbers();
        loadConfig();
      }
    } catch (error) {
      toast({ title: 'Errore sincronizzazione', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSyncingNumbers(false);
    }
  };

  const handleSyncWhatsAppSenders = async () => {
    setSyncingSenders(true);
    try {
      const res = await apiClient.post<{ synced: number }>('/messaging/whatsapp-senders/sync');
      if (res.success) {
        toast({ title: 'Sincronizzazione completata', description: `${res.data?.synced || 0} sender sincronizzati` });
        loadWhatsAppSenders();
        loadConfig();
      }
    } catch (error) {
      toast({ title: 'Errore sincronizzazione', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSyncingSenders(false);
    }
  };

  const handleConfigureWebhook = async (numberId: string, type: 'sms' | 'whatsapp') => {
    try {
      const res = await apiClient.put(`/messaging/senders/${numberId}/webhooks`);
      if (res.success) {
        toast({ title: 'Webhook configurato' });
        type === 'whatsapp' ? loadWhatsAppSenders() : loadTwilioNumbers();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleSearchAvailableNumbers = async () => {
    setSearchingNumbers(true);
    try {
      const res = await apiClient.get<{ numbers: TwilioAvailableNumber[] }>(`/messaging/twilio-numbers/available?country=${purchaseForm.country}&areaCode=${purchaseForm.areaCode}&type=${purchaseForm.numberType}`);
      if (res.success && res.data?.numbers) {
        setAvailableNumbers(res.data.numbers);
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSearchingNumbers(false);
    }
  };

  const handlePurchaseNumber = async (phoneNumber: string) => {
    setPurchasing(true);
    try {
      const res = await apiClient.post('/messaging/twilio-numbers/purchase', { phoneNumber });
      if (res.success) {
        toast({ title: 'Numero acquistato', description: `${phoneNumber} aggiunto al tuo account` });
        setPurchaseNumberOpen(false);
        setAvailableNumbers([]);
        loadTwilioNumbers();
      }
    } catch (error) {
      toast({ title: 'Errore acquisto', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setPurchasing(false);
    }
  };

  const handleDeleteTwilioNumber = async (id: string) => {
    if (!confirm('Eliminare questo numero? Verrà rilasciato dal tuo account Twilio.')) return;
    try {
      await apiClient.delete(`/messaging/senders/${id}`);
      toast({ title: 'Numero eliminato' });
      loadTwilioNumbers();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleAddWhatsAppSender = async () => {
    setAddingSender(true);
    try {
      const res = await apiClient.post('/messaging/senders', {
        ...senderForm,
        channel_type: 'whatsapp'
      });
      if (res.success) {
        toast({ title: 'Sender aggiunto' });
        setAddSenderOpen(false);
        setSenderForm({ senderId: '', displayName: '' });
        loadWhatsAppSenders();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setAddingSender(false);
    }
  };

  const handleDeleteWhatsAppSender = async (id: string) => {
    if (!confirm('Eliminare questo sender WhatsApp?')) return;
    try {
      await apiClient.delete(`/messaging/senders/${id}`);
      toast({ title: 'Sender eliminato' });
      loadWhatsAppSenders();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Eliminare questo template?')) return;
    try {
      await apiClient.delete(`/messaging/templates/${id}`);
      toast({ title: 'Template eliminato' });
      loadTemplates();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleRefreshTemplateStatus = async (id: string) => {
    try {
      await apiClient.get(`/messaging/templates/${id}/status`);
      loadTemplates();
      toast({ title: 'Status aggiornato' });
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Approvato</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" />In Attesa</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Rifiutato</Badge>;
      default:
        return <Badge variant="outline"><FileText className="h-3 w-3 mr-1" />Bozza</Badge>;
    }
  };

  const getSyncBadge = (contentSid?: string) => {
    if (contentSid) {
      return <Badge className="bg-blue-100 text-blue-700"><CheckCircle className="h-3 w-3 mr-1" />Twilio</Badge>;
    }
    return <Badge variant="outline" className="text-muted-foreground">Solo Locale</Badge>;
  };

  const handleSyncFromTwilio = async () => {
    setSyncing(true);
    try {
      const res = await apiClient.post<{ imported: number; updated: number; skipped: number }>('/messaging/templates/twilio/sync');
      if (res.success && res.data) {
        toast({ 
          title: 'Sincronizzazione completata',
          description: `Importati: ${res.data.imported}, Aggiornati: ${res.data.updated}, Ignorati: ${res.data.skipped}`
        });
        loadTemplates();
      }
    } catch (error) {
      toast({ title: 'Errore sincronizzazione', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handlePushToTwilio = async (id: string) => {
    setTemplateActionLoading(id);
    try {
      const res = await apiClient.post<{ contentSid: string; message: string }>(`/messaging/templates/${id}/twilio/push`);
      if (res.success) {
        toast({ title: 'Successo', description: res.data?.message || 'Template caricato su Twilio' });
        loadTemplates();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTemplateActionLoading(null);
    }
  };

  const handleUpdateOnTwilio = async (id: string) => {
    setTemplateActionLoading(id);
    try {
      const res = await apiClient.post<{ message: string }>(`/messaging/templates/${id}/twilio/update`);
      if (res.success) {
        toast({ title: 'Successo', description: res.data?.message || 'Template aggiornato su Twilio' });
        loadTemplates();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTemplateActionLoading(null);
    }
  };

  const handleDeleteFromTwilio = async (id: string, deleteLocal: boolean) => {
    const confirmMsg = deleteLocal 
      ? 'Eliminare il template da Twilio E dal database locale?' 
      : 'Eliminare il template da Twilio? (Rimarrà come bozza locale)';
    if (!confirm(confirmMsg)) return;
    
    setTemplateActionLoading(id);
    try {
      const res = await apiClient.delete<{ message: string }>(`/messaging/templates/${id}/twilio?deleteLocal=${deleteLocal}`);
      if (res.success) {
        toast({ title: 'Successo', description: res.data?.message || 'Template eliminato da Twilio' });
        loadTemplates();
      }
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setTemplateActionLoading(null);
    }
  };

  const filteredTemplates = templates.filter(t => 
    templateFilter === 'all' || t.approval_status === templateFilter
  );

  const handleOpenAddPhone = (phone?: LocalPhoneNumber) => {
    if (phone) {
      setPhoneForm({ 
        phone_number: phone.phone_number, 
        type: phone.type, 
        label: phone.label || '', 
        is_default: phone.is_default 
      });
      setPhoneDialog({ open: true, editing: phone });
    } else {
      setPhoneForm({ phone_number: '', type: 'sms', label: '', is_default: false });
      setPhoneDialog({ open: true, editing: null });
    }
  };

  const handleSavePhone = async () => {
    if (!phoneForm.phone_number) {
      toast({ title: 'Errore', description: 'Inserisci il numero di telefono', variant: 'destructive' });
      return;
    }
    setSavingPhone(true);
    try {
      if (phoneDialog.editing) {
        await apiClient.put(`/messaging/senders/${phoneDialog.editing.id}`, phoneForm);
        toast({ title: 'Numero aggiornato' });
      } else {
        await apiClient.post('/messaging/senders', phoneForm);
        toast({ title: 'Numero aggiunto' });
      }
      setPhoneDialog({ open: false, editing: null });
      loadConfig();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    } finally {
      setSavingPhone(false);
    }
  };

  const handleDeletePhone = async (id: string) => {
    if (!confirm('Eliminare questo numero?')) return;
    try {
      await apiClient.delete(`/messaging/senders/${id}`);
      toast({ title: 'Numero eliminato' });
      loadConfig();
    } catch (error) {
      toast({ title: 'Errore', description: getErrorMessage(error), variant: 'destructive' });
    }
  };

  const handleSaveCredentials = async () => {
    if (!credentials.account_sid) {
      toast({
        title: 'Errore',
        description: 'Account SID è obbligatorio',
        variant: 'destructive'
      });
      return;
    }
    
    // Require at least one auth method
    const hasAuthToken = !!credentials.auth_token?.trim();
    const hasApiKey = !!credentials.api_key?.trim() && !!credentials.api_secret?.trim();
    
    if (!hasAuthToken && !hasApiKey) {
      toast({
        title: 'Metodo di autenticazione richiesto',
        description: 'Inserisci almeno Auth Token oppure API Key + API Secret',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.post('/messaging/settings', credentials);
      if (response.success) {
        toast({
          title: 'Credenziali salvate',
          description: 'Le credenziali Twilio sono state configurate correttamente'
        });
        loadConfig();
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile salvare le credenziali',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await apiClient.post<{ success: boolean; account_name?: string; error?: string }>('/messaging/settings/test');
      if (response.success && response.data?.success) {
        toast({
          title: 'Connessione riuscita',
          description: `Account Twilio: ${response.data.account_name || 'Verificato'}`
        });
        loadConfig();
      } else {
        toast({
          title: 'Test fallito',
          description: response.data?.error || response.error || 'Connessione fallita',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile testare la connessione',
        variant: 'destructive'
      });
    } finally {
      setTesting(false);
    }
  };

  const getConnectionBadge = () => {
    if (!config?.configured) {
      return <Badge variant="outline" className="ml-2"><AlertTriangle className="h-3 w-3 mr-1" />Non configurato</Badge>;
    }
    switch (config.connection_status) {
      case 'healthy':
        return <Badge className="bg-green-100 text-green-700 ml-2"><CheckCircle className="h-3 w-3 mr-1" />Connesso</Badge>;
      case 'unhealthy':
        return <Badge className="bg-red-100 text-red-700 ml-2"><XCircle className="h-3 w-3 mr-1" />Errore</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700 ml-2"><AlertTriangle className="h-3 w-3 mr-1" />Da verificare</Badge>;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiato negli appunti' });
  };

  const runAllApiTests = async () => {
    setApiTestRunning(true);
    setApiTestResults([]);
    setApiTestSummary(null);
    try {
      const response = await apiClient.post<{
        success: boolean;
        results: ApiTestResult[];
        summary: TestSummary;
      }>('/messaging/test-all');
      
      if (response.success && response.data) {
        setApiTestResults(response.data.results);
        setApiTestSummary(response.data.summary);
        toast({
          title: response.data.summary.error === 0 ? 'Test completati' : 'Test con errori',
          description: `${response.data.summary.success}/${response.data.summary.total} test superati`,
          variant: response.data.summary.error > 0 ? 'destructive' : 'default'
        });
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile eseguire i test',
        variant: 'destructive'
      });
    } finally {
      setApiTestRunning(false);
    }
  };

  const runWebhookTest = async () => {
    setWebhookTestRunning(true);
    setWebhookTestResults([]);
    try {
      let payload = {};
      if (webhookTestPayload.trim()) {
        try {
          payload = JSON.parse(webhookTestPayload);
        } catch {
          toast({
            title: 'JSON non valido',
            description: 'Il payload deve essere un JSON valido',
            variant: 'destructive'
          });
          setWebhookTestRunning(false);
          return;
        }
      }
      
      const response = await apiClient.post<WebhookTestResult>('/messaging/test-webhook', {
        webhookType: webhookTestType,
        testPayload: Object.keys(payload).length > 0 ? payload : undefined
      });
      
      if (response.success && response.data) {
        setWebhookTestResults(response.data.results);
        if (!webhookTestPayload.trim() && response.data.samplePayloads?.[webhookTestType]) {
          setWebhookTestPayload(JSON.stringify(response.data.samplePayloads[webhookTestType], null, 2));
        }
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile testare webhook',
        variant: 'destructive'
      });
    } finally {
      setWebhookTestRunning(false);
    }
  };

  const toggleTestExpanded = (testName: string) => {
    setExpandedTests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testName)) {
        newSet.delete(testName);
      } else {
        newSet.add(testName);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'skipped':
        return <Clock className="h-5 w-5 text-muted-foreground" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getTestStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-700">OK</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700">Errore</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-100 text-yellow-700">Attenzione</Badge>;
      case 'skipped':
        return <Badge variant="outline">Saltato</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'account': return 'Account';
      case 'sms': return 'SMS';
      case 'whatsapp': return 'WhatsApp';
      case 'templates': return 'Template';
      case 'senders': return 'Sender';
      case 'webhook': return 'Webhook';
      default: return category;
    }
  };

  const loadAssignments = async () => {
    try {
      setAssignLoading(true);
      const [sendersRes, usersRes] = await Promise.all([
        apiClient.get<{ senders: AssignSender[] }>('/messaging/senders?includeDeleted=false&adminView=true'),
        apiClient.get<{ id: string; first_name?: string; last_name?: string; email?: string }[]>('/messaging/sender-assignments/users'),
      ]);
      const senders = sendersRes.data?.senders || [];
      setAssignSenders(senders);
      const users = usersRes.data || [];
      setAssignTenantUsers(Array.isArray(users) ? users : []);

      const assignMap: Record<string, string[]> = {};
      const globalMap: Record<string, boolean> = {};
      for (const s of senders) {
        try {
          const aRes = await apiClient.get<{ is_global?: boolean; assignments?: SenderAssignment[] } | SenderAssignment[]>(`/messaging/senders/${s.id}/assignments`);
          // La rotta ora restituisce { is_global, assignments: [...] }; manteniamo
          // un fallback per compatibilità durante il rolling deploy.
          const payload = aRes.data ?? {};
          const assignments = Array.isArray(payload) ? payload : (payload.assignments || []);
          assignMap[s.id] = Array.isArray(assignments) ? assignments.map((a: SenderAssignment) => a.user_id) : [];
          globalMap[s.id] = (!Array.isArray(payload) && typeof payload.is_global === 'boolean')
            ? payload.is_global
            : (s.is_global !== false);
        } catch {
          assignMap[s.id] = [];
          globalMap[s.id] = s.is_global !== false;
        }
      }
      setAssignData(assignMap);
      setAssignGlobal(globalMap);
    } catch (err: unknown) {
      toast({ title: 'Errore', description: 'Impossibile caricare assegnazioni', variant: 'destructive' });
    } finally {
      setAssignLoading(false);
    }
  };

  const handleSaveAssignment = async (senderId: string) => {
    try {
      setAssignSaving(senderId);
      const isGlobal = assignGlobal[senderId] !== false;
      // Quando il sender è marcato globale ignoriamo eventuali utenti
      // selezionati lato UI: la regola "Tutti" prevale.
      const res = await apiClient.put<{ success?: boolean }>(`/messaging/senders/${senderId}/assignments`, {
        user_ids: isGlobal ? [] : (assignData[senderId] || []),
        is_global: isGlobal,
      });
      if (res.success) {
        toast({ title: 'Salvato', description: 'Assegnazione aggiornata' });
      }
    } catch (err) {
      toast({ title: 'Errore', description: getErrorMessage(err), variant: 'destructive' });
    } finally {
      setAssignSaving(null);
    }
  };

  const toggleAssignUser = (senderId: string, userId: string) => {
    setAssignData(prev => {
      const current = prev[senderId] || [];
      const updated = current.includes(userId) ? current.filter(id => id !== userId) : [...current, userId];
      return { ...prev, [senderId]: updated };
    });
  };

  const webhookBaseUrl = getPublicBaseUrl();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Impostazioni Messaggistica</h1>
          <p className="text-muted-foreground">Configura credenziali Twilio, numeri e webhook</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadConfig}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      <Tabs defaultValue="credentials" className="space-y-6">
        <TabsList>
          <TabsTrigger value="credentials">
            <Key className="h-4 w-4 mr-2" />
            Credenziali
          </TabsTrigger>
          <TabsTrigger value="numbers">
            <Phone className="h-4 w-4 mr-2" />
            Numeri
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="h-4 w-4 mr-2" />
            Template
          </TabsTrigger>
          <TabsTrigger value="media">
            <Image className="h-4 w-4 mr-2" />
            Media
          </TabsTrigger>
          <TabsTrigger value="webhooks">
            <Webhook className="h-4 w-4 mr-2" />
            Webhook
          </TabsTrigger>
          <TabsTrigger value="assignments" onClick={() => { if (assignSenders.length === 0) loadAssignments(); }}>
            <Users className="h-4 w-4 mr-2" />
            Assegnazione
          </TabsTrigger>
          <TabsTrigger value="test">
            <Activity className="h-4 w-4 mr-2" />
            Test
          </TabsTrigger>
        </TabsList>

        <TabsContent value="credentials" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    Credenziali Twilio
                    {getConnectionBadge()}
                  </CardTitle>
                  <CardDescription>
                    Configura le credenziali API per connetterti a Twilio
                  </CardDescription>
                </div>
                {config?.mcp_synced && (
                  <Badge variant="outline" className="text-purple-600 border-purple-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Sync MCP
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="account_sid">Account SID</Label>
                <Input
                  id="account_sid"
                  placeholder="AC..."
                  value={credentials.account_sid}
                  onChange={(e) => setCredentials(prev => ({ ...prev, account_sid: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Trovi l'Account SID nella dashboard principale di Twilio
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="auth_token">Auth Token</Label>
                <div className="relative">
                  <Input
                    id="auth_token"
                    type={showSecrets ? 'text' : 'password'}
                    placeholder={config?.has_auth_token ? '••••••••••••••••••••••••••••••••' : 'Il tuo Auth Token'}
                    value={credentials.auth_token}
                    onChange={(e) => setCredentials(prev => ({ ...prev, auth_token: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowSecrets(!showSecrets)}
                  >
                    {showSecrets ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {config?.has_auth_token && !credentials.auth_token && (
                  <p className="text-xs text-green-600">Auth Token salvato. Lascia vuoto per mantenere quello esistente.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="api_key">API Key (opzionale)</Label>
                <Input
                  id="api_key"
                  placeholder="SK..."
                  value={credentials.api_key}
                  onChange={(e) => setCredentials(prev => ({ ...prev, api_key: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="api_secret">API Secret (opzionale)</Label>
                <Input
                  id="api_secret"
                  type="password"
                  placeholder="Il tuo API Secret"
                  value={credentials.api_secret}
                  onChange={(e) => setCredentials(prev => ({ ...prev, api_secret: e.target.value }))}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button onClick={handleSaveCredentials} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Salva Credenziali
                </Button>
                {config?.configured && (
                  <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
                    {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Test Connessione
                  </Button>
                )}
              </div>

              <Alert className="mt-4">
                <ExternalLink className="h-4 w-4" />
                <AlertTitle>Come ottenere le credenziali</AlertTitle>
                <AlertDescription>
                  1. Accedi alla <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Console Twilio</a><br />
                  2. L'Account SID si trova nella dashboard principale<br />
                  3. Per le API Key, vai su Account → API keys & tokens → Create API key
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="numbers" className="space-y-4">
          <div className="flex gap-2 mb-4">
            <Button 
              variant={numbersTab === 'twilio' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => { setNumbersTab('twilio'); loadTwilioNumbers(); }}
            >
              <Phone className="h-4 w-4 mr-2" />
              Twilio SMS
            </Button>
            <Button 
              variant={numbersTab === 'whatsapp' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => { setNumbersTab('whatsapp'); loadWhatsAppSenders(); }}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          {numbersTab === 'twilio' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Numeri Telefono Twilio</CardTitle>
                    <CardDescription>
                      Acquista e gestisci numeri SMS dal tuo account Twilio
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={handleSyncTwilioNumbers} disabled={syncingNumbers}>
                      {syncingNumbers ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Sincronizza
                    </Button>
                    <Button type="button" onClick={() => setPurchaseNumberOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Acquista Numero
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {twilioNumbers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Phone className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun numero Twilio trovato</p>
                    <p className="text-sm">Sincronizza da Twilio o acquista un nuovo numero</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {twilioNumbers.map((num) => (
                      <div key={num.id} className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-mono font-medium text-lg">{num.phone_number}</p>
                              <Badge className={num.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                {num.status === 'active' ? 'Attivo' : num.status}
                              </Badge>
                            </div>
                            {num.friendly_name && <p className="text-sm text-muted-foreground">{num.friendly_name}</p>}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {num.country_code && <Badge variant="outline">{num.country_code}</Badge>}
                              {num.number_type && <Badge variant="outline">{num.number_type}</Badge>}
                              {num.capabilities?.sms && <Badge className="bg-blue-50 text-blue-700">SMS</Badge>}
                              {num.capabilities?.mms && <Badge className="bg-purple-50 text-purple-700">MMS</Badge>}
                              {num.capabilities?.voice && <Badge className="bg-green-50 text-green-700">Voice</Badge>}
                              {num.webhook_configured ? (
                                <Badge className="bg-blue-100 text-blue-700"><CheckCircle className="h-3 w-3 mr-1" />Webhook OK</Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600"><AlertTriangle className="h-3 w-3 mr-1" />Webhook</Badge>
                              )}
                            </div>
                            {num.monthly_cost && (
                              <p className="text-xs text-muted-foreground mt-1">${num.monthly_cost}/mese</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!num.webhook_configured && (
                            <Button type="button" variant="outline" size="sm" onClick={() => handleConfigureWebhook(num.id, 'sms')}>
                              <Webhook className="h-4 w-4 mr-1" />
                              Webhook
                            </Button>
                          )}
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteTwilioNumber(num.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {numbersTab === 'whatsapp' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>WhatsApp Senders</CardTitle>
                    <CardDescription>
                      Gestisci i numeri WhatsApp Business registrati su Twilio
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={handleSyncWhatsAppSenders} disabled={syncingSenders}>
                      {syncingSenders ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Sincronizza
                    </Button>
                    <Button type="button" onClick={() => setAddSenderOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Aggiungi Sender
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {whatsappSenders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun sender WhatsApp trovato</p>
                    <p className="text-sm">Sincronizza da Twilio o aggiungi un sender manualmente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {whatsappSenders.map((sender) => (
                      <div key={sender.id} className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent">
                        <div className="flex items-center gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-mono font-medium text-lg">{(sender.phone_number || sender.sender_id || '').replace('whatsapp:', '')}</p>
                              <Badge className={sender.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                                {sender.status === 'active' ? 'Attivo' : sender.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{sender.display_name}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {sender.verification_status && (
                                <Badge variant="outline">{sender.verification_status}</Badge>
                              )}
                              {sender.quality_rating && (
                                <Badge variant="outline" className="text-purple-600">{sender.quality_rating}</Badge>
                              )}
                              {sender.messaging_limit && (
                                <Badge variant="outline" className="text-blue-600">{sender.messaging_limit}</Badge>
                              )}
                              {sender.webhook_configured ? (
                                <Badge className="bg-blue-100 text-blue-700"><CheckCircle className="h-3 w-3 mr-1" />Webhook OK</Badge>
                              ) : (
                                <Badge variant="outline" className="text-orange-600"><AlertTriangle className="h-3 w-3 mr-1" />Webhook</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!sender.webhook_configured && (
                            <Button type="button" variant="outline" size="sm" onClick={() => handleConfigureWebhook(sender.id, 'whatsapp')}>
                              <Webhook className="h-4 w-4 mr-1" />
                              Webhook
                            </Button>
                          )}
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteWhatsAppSender(sender.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Dialog open={phoneDialog.open} onOpenChange={(open) => setPhoneDialog({ open, editing: null })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{phoneDialog.editing ? 'Modifica Numero' : 'Aggiungi Numero'}</DialogTitle>
                <DialogDescription>Configura un numero per SMS o WhatsApp</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Numero Telefono</Label>
                  <Input placeholder="+39 02 1234567" value={phoneForm.phone_number} onChange={(e) => setPhoneForm(prev => ({ ...prev, phone_number: e.target.value }))} />
                  <p className="text-xs text-muted-foreground">Formato E.164 (es. +391234567890)</p>
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <select className="w-full p-2 border rounded-md" value={phoneForm.type} onChange={(e) => setPhoneForm(prev => ({ ...prev, type: e.target.value as 'sms' | 'whatsapp' | 'both' }))}>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="both">SMS + WhatsApp</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Etichetta (opzionale)</Label>
                  <Input placeholder="Es. Reception, Booking" value={phoneForm.label} onChange={(e) => setPhoneForm(prev => ({ ...prev, label: e.target.value }))} />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={phoneForm.is_default} onCheckedChange={(checked) => setPhoneForm(prev => ({ ...prev, is_default: checked }))} />
                  <Label>Numero predefinito</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPhoneDialog({ open: false, editing: null })}>Annulla</Button>
                <Button onClick={handleSavePhone} disabled={savingPhone}>
                  {savingPhone && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {phoneDialog.editing ? 'Salva' : 'Aggiungi'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={purchaseNumberOpen} onOpenChange={setPurchaseNumberOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Acquista Numero Twilio</DialogTitle>
                <DialogDescription>Cerca e acquista un nuovo numero telefonico</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Paese</Label>
                    <select className="w-full p-2 border rounded-md" value={purchaseForm.country} onChange={(e) => setPurchaseForm(prev => ({ ...prev, country: e.target.value }))}>
                      <option value="US">Stati Uniti</option>
                      <option value="GB">Regno Unito</option>
                      <option value="IT">Italia</option>
                      <option value="DE">Germania</option>
                      <option value="FR">Francia</option>
                      <option value="ES">Spagna</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prefisso (opzionale)</Label>
                    <Input placeholder="415" value={purchaseForm.areaCode} onChange={(e) => setPurchaseForm(prev => ({ ...prev, areaCode: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo</Label>
                    <select className="w-full p-2 border rounded-md" value={purchaseForm.numberType} onChange={(e) => setPurchaseForm(prev => ({ ...prev, numberType: e.target.value }))}>
                      <option value="local">Locale</option>
                      <option value="mobile">Mobile</option>
                      <option value="tollFree">Toll-Free</option>
                    </select>
                  </div>
                </div>
                <Button type="button" onClick={handleSearchAvailableNumbers} disabled={searchingNumbers}>
                  {searchingNumbers ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Cerca Numeri Disponibili
                </Button>

                {availableNumbers.length > 0 && (
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {availableNumbers.map((num, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-accent">
                        <div>
                          <p className="font-mono font-medium">{num.phoneNumber}</p>
                          <div className="flex gap-2 mt-1">
                            {num.capabilities?.sms && <Badge className="bg-blue-50 text-blue-700 text-xs">SMS</Badge>}
                            {num.capabilities?.mms && <Badge className="bg-purple-50 text-purple-700 text-xs">MMS</Badge>}
                            {num.capabilities?.voice && <Badge className="bg-green-50 text-green-700 text-xs">Voice</Badge>}
                          </div>
                        </div>
                        <Button type="button" size="sm" onClick={() => handlePurchaseNumber(num.phoneNumber)} disabled={purchasing}>
                          {purchasing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Acquista'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setPurchaseNumberOpen(false); setAvailableNumbers([]); }}>Chiudi</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={addSenderOpen} onOpenChange={setAddSenderOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Aggiungi WhatsApp Sender</DialogTitle>
                <DialogDescription>Aggiungi manualmente un sender WhatsApp Business</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Numero Telefono (Sender ID)</Label>
                  <Input placeholder="+14155238886" value={senderForm.senderId} onChange={(e) => setSenderForm(prev => ({ ...prev, senderId: e.target.value }))} />
                  <p className="text-xs text-muted-foreground">Formato E.164 (es. +14155238886)</p>
                </div>
                <div className="space-y-2">
                  <Label>Nome Display</Label>
                  <Input placeholder="Hotel Reception" value={senderForm.displayName} onChange={(e) => setSenderForm(prev => ({ ...prev, displayName: e.target.value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddSenderOpen(false)}>Annulla</Button>
                <Button onClick={handleAddWhatsAppSender} disabled={addingSender || !senderForm.senderId}>
                  {addingSender && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Aggiungi Sender
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Template WhatsApp</CardTitle>
                  <CardDescription>
                    Crea e gestisci template per aprire conversazioni WhatsApp (policy 24h)
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleSyncFromTwilio} disabled={syncing}>
                    {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Sync da Twilio
                  </Button>
                  <Button onClick={() => setTemplateBuilderOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Nuovo Template
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <Select value={templateFilter} onValueChange={setTemplateFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filtra per stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="draft">Bozze</SelectItem>
                    <SelectItem value="pending">In Attesa</SelectItem>
                    <SelectItem value="approved">Approvati</SelectItem>
                    <SelectItem value="rejected">Rifiutati</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={loadTemplates} disabled={templatesLoading}>
                  {templatesLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>

              {filteredTemplates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun template trovato</p>
                  <p className="text-sm">Crea il tuo primo template per iniziare</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {filteredTemplates.map(template => (
                      <Card key={template.id} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-medium">{template.friendly_name}</span>
                              {getStatusBadge(template.approval_status)}
                              {getSyncBadge(template.content_sid)}
                              <Badge variant="outline">{template.category}</Badge>
                              <Badge variant="outline">{template.language.toUpperCase()}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {template.body_text}
                            </p>
                            {template.rejection_reason && (
                              <p className="text-sm text-red-600 mt-1">
                                <AlertTriangle className="h-3 w-3 inline mr-1" />
                                {template.rejection_reason}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Creato: {new Date(template.created_at).toLocaleDateString('it')}
                              {template.approved_at && ` | Approvato: ${new Date(template.approved_at).toLocaleDateString('it')}`}
                            </p>
                            {template.content_sid && (
                              <div className="flex items-center gap-2 mt-1">
                                <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                                  {template.content_sid}
                                </code>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-6 w-6 p-0"
                                  onClick={() => copyToClipboard(template.content_sid!)}
                                  title="Copia ContentSid"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 ml-4">
                            {templateActionLoading === template.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                {template.approval_status === 'pending' && (
                                  <Button size="sm" variant="ghost" onClick={() => handleRefreshTemplateStatus(template.id)} title="Aggiorna stato">
                                    <RefreshCw className="h-4 w-4" />
                                  </Button>
                                )}
                                {!template.content_sid && template.approval_status === 'draft' && (
                                  <Button size="sm" variant="ghost" onClick={() => handlePushToTwilio(template.id)} title="Carica su Twilio">
                                    <ExternalLink className="h-4 w-4 text-blue-600" />
                                  </Button>
                                )}
                                {template.content_sid && template.approval_status !== 'approved' && (
                                  <Button size="sm" variant="ghost" onClick={() => handleUpdateOnTwilio(template.id)} title="Aggiorna su Twilio">
                                    <RefreshCw className="h-4 w-4 text-blue-600" />
                                  </Button>
                                )}
                                {template.content_sid && template.approval_status === 'draft' && (
                                  <Button size="sm" variant="ghost" onClick={() => {
                                    apiClient.post(`/messaging/templates/${template.id}/submit`).then(() => {
                                      toast({ title: 'Template inviato per approvazione' });
                                      loadTemplates();
                                    });
                                  }} title="Invia per approvazione WhatsApp">
                                    <Send className="h-4 w-4" />
                                  </Button>
                                )}
                                {template.content_sid && (
                                  <Button size="sm" variant="ghost" onClick={() => handleDeleteFromTwilio(template.id, false)} title="Elimina da Twilio">
                                    <XCircle className="h-4 w-4 text-orange-500" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(template.id)} title="Elimina">
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}

              <Alert className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  I template WhatsApp richiedono l'approvazione di Meta (5 min - 24 ore).
                  Solo i template approvati possono essere usati per aprire nuove conversazioni.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        <TemplateBuilder
          open={templateBuilderOpen}
          onClose={() => setTemplateBuilderOpen(false)}
          onSuccess={loadTemplates}
        />

        <TabsContent value="media" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle>Libreria Media</CardTitle>
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex items-center justify-center rounded-full w-5 h-5 bg-muted hover:bg-muted-foreground/20 transition-colors">
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-sm p-3 text-sm leading-relaxed" sideOffset={8}>
                      <p className="font-semibold mb-1.5">Come funzionano i media nei template</p>
                      <p className="mb-2">I template WhatsApp supportano header con immagini, video o documenti. Hai due modalit&agrave;:</p>
                      <p className="mb-1"><span className="font-medium">1. URL esterno:</span> Inserisci direttamente un link pubblico al file. Il file deve essere accessibile da Meta per la validazione.</p>
                      <p className="mb-1"><span className="font-medium">2. Libreria Media:</span> Salva qui i tuoi media per riutilizzarli facilmente. Quando crei un template, seleziona dalla libreria invece di cercare ogni volta l'URL.</p>
                      <p className="mt-2 text-muted-foreground text-xs">Formati: JPG/PNG (max 5MB), MP4 (max 16MB), PDF (max 100MB). I media devono essere su URL pubblici HTTPS.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <CardDescription>
                Gestisci immagini, video e documenti per i tuoi template WhatsApp
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MediaLibrary />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configurazione Webhook</CardTitle>
              <CardDescription>
                URL per ricevere notifiche di messaggi in arrivo e aggiornamenti di stato
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <ExternalLink className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-700">
                  Configura questi URL nella Console Twilio per ogni numero telefonico
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>URL Webhook SMS (Messaggi in arrivo)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={`${webhookBaseUrl}/api/webhooks/twilio/sms`}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${webhookBaseUrl}/api/webhooks/twilio/sms`)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>URL Webhook Status (Aggiornamenti consegna)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={`${webhookBaseUrl}/api/webhooks/twilio/status`}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${webhookBaseUrl}/api/webhooks/twilio/status`)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>URL Webhook WhatsApp</Label>
                  <div className="flex gap-2">
                    <Input
                      value={`${webhookBaseUrl}/api/webhooks/twilio/whatsapp`}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${webhookBaseUrl}/api/webhooks/twilio/whatsapp`)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-muted rounded-lg p-4 mt-4">
                <h4 className="font-medium mb-2">Come configurare i webhook su Twilio:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Vai alla <a href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Console Twilio - Numeri</a></li>
                  <li>Clicca sul numero che vuoi configurare</li>
                  <li>Nella sezione "Messaging", incolla l'URL Webhook SMS</li>
                  <li>Nella sezione "Status Callback URL", incolla l'URL Status</li>
                  <li>Salva le modifiche</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Test Connessione API Twilio
                  </CardTitle>
                  <CardDescription>
                    Verifica tutte le API Twilio configurate per il tuo account
                  </CardDescription>
                </div>
                <Button onClick={runAllApiTests} disabled={apiTestRunning}>
                  {apiTestRunning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  Esegui Test
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {apiTestSummary && (
                <div className="mb-6 p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="font-medium">{apiTestSummary.success} OK</span>
                      </div>
                      {apiTestSummary.error > 0 && (
                        <div className="flex items-center gap-2">
                          <XCircle className="h-5 w-5 text-red-500" />
                          <span className="font-medium">{apiTestSummary.error} Errori</span>
                        </div>
                      )}
                      {apiTestSummary.warning > 0 && (
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-yellow-500" />
                          <span className="font-medium">{apiTestSummary.warning} Warning</span>
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Tempo totale: {apiTestSummary.totalDurationMs}ms
                    </span>
                  </div>
                </div>
              )}

              {apiTestResults.length === 0 && !apiTestRunning && (
                <div className="text-center py-12 text-muted-foreground">
                  <Activity className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Clicca "Esegui Test" per verificare le API Twilio</p>
                </div>
              )}

              {apiTestRunning && (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
                  <p className="mt-4 text-muted-foreground">Test in corso...</p>
                </div>
              )}

              {apiTestResults.length > 0 && !apiTestRunning && (
                <div className="space-y-3">
                  {['account', 'sms', 'whatsapp', 'templates', 'webhook'].map(category => {
                    const categoryResults = apiTestResults.filter(r => r.category === category);
                    if (categoryResults.length === 0) return null;
                    
                    return (
                      <div key={category} className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                          {getCategoryLabel(category)}
                        </h4>
                        {categoryResults.map((result, idx) => (
                          <div
                            key={`${result.name}-${idx}`}
                            className="border rounded-lg overflow-hidden"
                          >
                            <button
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
                              onClick={() => toggleTestExpanded(result.name)}
                            >
                              <div className="flex items-center gap-3">
                                {getStatusIcon(result.status)}
                                <span className="font-medium">{result.name}</span>
                                {getTestStatusBadge(result.status)}
                              </div>
                              <div className="flex items-center gap-3">
                                {result.durationMs && (
                                  <span className="text-xs text-muted-foreground">
                                    {result.durationMs}ms
                                  </span>
                                )}
                                {expandedTests.has(result.name) ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </div>
                            </button>
                            {expandedTests.has(result.name) && (
                              <div className="px-4 py-3 bg-muted/30 border-t">
                                <p className="text-sm mb-2">{result.message}</p>
                                {result.error && (
                                  <Alert variant="destructive" className="mb-2">
                                    <AlertDescription className="text-xs font-mono">
                                      {result.error}
                                    </AlertDescription>
                                  </Alert>
                                )}
                                {result.details && (
                                  <div className="bg-background rounded p-3 mt-2">
                                    <pre className="text-xs overflow-auto max-h-48">
                                      {JSON.stringify(result.details, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Test Simulatore Webhook
                  </CardTitle>
                  <CardDescription>
                    Simula payload webhook per verificare la corretta elaborazione
                  </CardDescription>
                </div>
                <Button onClick={runWebhookTest} disabled={webhookTestRunning}>
                  {webhookTestRunning ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Testa Webhook
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Tipo Webhook</Label>
                  <Select value={webhookTestType} onValueChange={setWebhookTestType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sms_incoming">SMS Incoming</SelectItem>
                      <SelectItem value="whatsapp_incoming">WhatsApp Incoming</SelectItem>
                      <SelectItem value="status_callback">Status Callback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Payload di Test (JSON)</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setWebhookTestPayload('')}
                  >
                    Usa Default
                  </Button>
                </div>
                <textarea
                  className="w-full h-40 p-3 font-mono text-xs border rounded-lg bg-muted/30"
                  value={webhookTestPayload}
                  onChange={(e) => setWebhookTestPayload(e.target.value)}
                  placeholder="Lascia vuoto per usare il payload di esempio..."
                />
              </div>

              {webhookTestResults.length > 0 && (
                <div className="space-y-2">
                  <Label>Risultati Validazione</Label>
                  <div className="space-y-2">
                    {webhookTestResults.map((result, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {getStatusIcon(result.status)}
                          <span className="font-medium">{result.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">{result.message}</span>
                          {getTestStatusBadge(result.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Assegnazione Numeri SMS/WhatsApp
              </CardTitle>
              <CardDescription>
                Per ogni numero scegli se è disponibile a tutti gli utenti del tenant
                oppure ristretto a una lista esplicita di assegnatari.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : assignSenders.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  Nessun numero configurato. Configura i numeri nella tab "Numeri".
                </div>
              ) : (
                <div className="space-y-4">
                  {assignSenders.map((sender: AssignSender) => {
                    const assigned = assignData[sender.id] || [];
                    const isGlobal = assignGlobal[sender.id] !== false;
                    return (
                      <Card key={sender.id} className="border">
                        <CardContent className="pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Badge variant={sender.channel_type === 'whatsapp' ? 'default' : 'secondary'}>
                                {sender.channel_type === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                              </Badge>
                              <span className="font-medium">{sender.phone_number}</span>
                              {sender.display_name && (
                                <span className="text-sm text-muted-foreground">({sender.display_name})</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isGlobal ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Tutti</Badge>
                              ) : (
                                <Badge variant="outline">{assigned.length} utent{assigned.length === 1 ? 'e' : 'i'}</Badge>
                              )}
                              <Button
                                size="sm"
                                onClick={() => handleSaveAssignment(sender.id)}
                                disabled={assignSaving === sender.id}
                              >
                                {assignSaving === sender.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                                <span className="ml-1">Salva</span>
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 mb-3 p-2 rounded-md bg-muted border">
                            <Switch
                              checked={isGlobal}
                              onCheckedChange={(v) => setAssignGlobal(prev => ({ ...prev, [sender.id]: v }))}
                            />
                            <div className="text-sm">
                              <div className="font-medium">Disponibile a tutti</div>
                              <div className="text-xs text-muted-foreground">
                                {isGlobal
                                  ? 'Ogni utente del tenant può inviare con questo numero.'
                                  : 'Solo gli utenti selezionati qui sotto possono inviare con questo numero.'}
                              </div>
                            </div>
                          </div>
                          <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 ${isGlobal ? 'opacity-50 pointer-events-none' : ''}`}>
                            {assignTenantUsers.map((u: AssignTenantUser) => {
                              const isSelected = assigned.includes(u.id);
                              return (
                                <button
                                  key={u.id}
                                  onClick={() => toggleAssignUser(sender.id, u.id)}
                                  disabled={isGlobal}
                                  className={`flex items-center gap-2 p-2 rounded-md border text-sm transition-colors text-left ${
                                    isSelected
                                      ? 'bg-blue-50 border-blue-300 text-blue-800'
                                      : 'bg-card border-border text-muted-foreground hover:bg-accent'
                                  }`}
                                >
                                  <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                                    isSelected ? 'bg-blue-600 border-blue-600' : 'border-border'
                                  }`}>
                                    {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                                  </div>
                                  <div className="truncate">
                                    <div className="font-medium truncate">{u.first_name} {u.last_name}</div>
                                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
}
